import type { TemplateRegistration, TemplateData } from './types';
import { sanitizeText, sanitizeSeoText, escapeAttr } from './sanitize';
import { getIconSvg, renderStars, buildFontsUrl, buildNavLinks, NavDataFlags, truncateAtSentence } from './helpers';

export const neonNights: TemplateRegistration = {
  meta: {
    id: 'neon-nights',
    name: 'Neon Nights',
    category: 'Modern',
    description: 'Cyberpunk-inspired design with neon glows, dark backgrounds, and electric accents',
    previewImage: 'https://images.unsplash.com/photo-1550745165-9bc0b252726f?w=800&q=80',
    supportedFeatures: ['contact-form', 'phone-display', 'address-map', 'business-hours', 'about-section', 'services-list', 'testimonials', 'google-reviews', 'social-links', 'faq', 'price-list', 'promotions', 'photo-gallery'],
    defaultEnabledFeatures: ['contact-form', 'phone-display', 'about-section', 'services-list', 'testimonials'],
  },
  render(data: TemplateData, enabledFeatures: Set<string>): string {
    const safeName = sanitizeText(data.businessName);
    const safeHeadline = sanitizeText(data.headline);
    const safeAbout = sanitizeText(data.aboutText);
    const safeSeoTitle = sanitizeSeoText(data.seo.title, 70);
    const safeSeoDesc = sanitizeSeoText(data.seo.description, 160);

    const phone = data.businessData?.phone || null;
    const contactEmail = data.businessData?.contactEmail || null;
    const rating = data.businessData?.rating || null;
    const reviewCount = data.businessData?.reviewCount || 0;
    const businessHours = data.businessData?.businessHours || [];
    const photos = data.businessData?.photos || [];
    const address = data.businessData?.address || '';
    const googleMapsUrl = data.businessData?.googleMapsUrl || null;
    const heroImage = photos[0] || null;
    const galleryImages = photos.slice(1, 6);

    const navDataFlags: NavDataFlags = {
      hasServices: data.services.length > 0,
      hasTestimonials: data.testimonials.length > 0,
      hasGalleryPhotos: galleryImages.length > 0,
      hasFaq: !!(data.faq && data.faq.length > 0),
      hasPriceList: !!(data.priceList && data.priceList.length > 0),
    };
    const navLinks = buildNavLinks(enabledFeatures, navDataFlags);
    const fontsUrl = buildFontsUrl(['Outfit', 'Inter']);

    const sections: string[] = [];

    // --- Google Reviews ---
    if (enabledFeatures.has('google-reviews') && rating) {
      sections.push(`
      <section class="google-reviews">
        <div class="container">
          <div class="review-badge">
            <div class="review-stars">${renderStars(Math.round(rating))}</div>
            <span class="review-text">${rating.toFixed(1)} / 5 &mdash; ${reviewCount} Google reviews</span>
          </div>
        </div>
      </section>`);
    }

    // --- Promotions ---
    if (enabledFeatures.has('promotions') && data.promotion) {
      const promoTitle = sanitizeText(data.promotion.title);
      const promoDesc = sanitizeText(data.promotion.description);
      const promoDiscount = sanitizeText(data.promotion.discount);
      sections.push(`
      <section class="promo-banner">
        <div class="container">
          <div class="promo-inner">
            <span class="promo-badge neon-cyan">${promoDiscount}</span>
            <div class="promo-content">
              <strong>${promoTitle}</strong>
              <span>${promoDesc}</span>
            </div>
          </div>
        </div>
      </section>`);
    }

    // --- About ---
    if (enabledFeatures.has('about-section')) {
      const statItems = rating ? `
            <div class="about-stats">
              <div class="stat-box"><span class="stat-value">${rating.toFixed(1)}</span><span class="stat-label">Rating</span></div>
              <div class="stat-box"><span class="stat-value">${reviewCount}+</span><span class="stat-label">Reviews</span></div>
              <div class="stat-box"><span class="stat-value">${data.services.length}</span><span class="stat-label">Services</span></div>
            </div>` : '';
      sections.push(`
      <section id="about" class="section">
        <div class="container">
          <h2 class="section-title">About Us <span class="neon-underline"></span></h2>
          <div class="about-card">
            <p class="about-text">${safeAbout}</p>
            ${statItems}
          </div>
        </div>
      </section>`);
    }

    // --- Services ---
    if (enabledFeatures.has('services-list') && data.services.length > 0) {
      const neonColors = ['var(--cyan)', 'var(--pink)', 'var(--yellow)', 'var(--cyan)', 'var(--pink)', 'var(--yellow)'];
      const serviceCards = data.services.map((s, i) => {
        const sName = sanitizeText(s.name);
        const sDesc = sanitizeText(s.description);
        const color = neonColors[i % neonColors.length];
        return `
          <div class="service-card" style="--card-neon: ${color}">
            <div class="service-top-border"></div>
            <div class="service-icon">${getIconSvg(s.icon || 'zap')}</div>
            <h3>${sName}</h3>
            <p>${sDesc}</p>
            ${s.price ? `<span class="service-price">${sanitizeText(s.price)}</span>` : ''}
          </div>`;
      }).join('');
      sections.push(`
      <section id="services" class="section">
        <div class="container">
          <h2 class="section-title">Services <span class="neon-underline"></span></h2>
          <div class="services-grid">${serviceCards}</div>
        </div>
      </section>`);
    }

    // --- Photo Gallery ---
    if (enabledFeatures.has('photo-gallery') && galleryImages.length > 0) {
      const imgs = galleryImages.map(url => `<div class="gallery-item"><img src="${escapeAttr(url)}" alt="${safeName}" loading="lazy"></div>`).join('');
      sections.push(`
      <section id="gallery" class="section">
        <div class="container">
          <h2 class="section-title">Gallery <span class="neon-underline"></span></h2>
          <div class="gallery-grid">${imgs}</div>
        </div>
      </section>`);
    }

    // --- Testimonials ---
    if (enabledFeatures.has('testimonials') && data.testimonials.length > 0) {
      const cards = data.testimonials.map(t => {
        const tName = sanitizeText(t.name);
        const tContent = sanitizeText(t.content);
        const tRole = t.role ? sanitizeText(t.role) : '';
        return `
          <div class="testimonial-card">
            <div class="neon-quote">&ldquo;</div>
            <p class="testimonial-body">${tContent}</p>
            <div class="testimonial-author">
              <strong>${tName}</strong>
              ${tRole ? `<span class="testimonial-role">${tRole}</span>` : ''}
              ${t.rating ? `<div class="testimonial-stars">${renderStars(t.rating)}</div>` : ''}
            </div>
          </div>`;
      }).join('');
      sections.push(`
      <section id="testimonials" class="section">
        <div class="container">
          <h2 class="section-title">Reviews <span class="neon-underline"></span></h2>
          <div class="testimonials-grid">${cards}</div>
        </div>
      </section>`);
    }

    // --- FAQ ---
    if (enabledFeatures.has('faq') && data.faq && data.faq.length > 0) {
      const items = data.faq.map(f => {
        const q = sanitizeText(f.question);
        const a = sanitizeText(f.answer);
        return `
          <div class="faq-item">
            <h3 class="faq-q">${q}</h3>
            <p class="faq-a">${a}</p>
          </div>`;
      }).join('');
      sections.push(`
      <section id="faq" class="section">
        <div class="container">
          <h2 class="section-title">FAQ <span class="neon-underline"></span></h2>
          <div class="faq-list">${items}</div>
        </div>
      </section>`);
    }

    // --- Price List ---
    if (enabledFeatures.has('price-list') && data.priceList && data.priceList.length > 0) {
      const rows = data.priceList.map(p => {
        const pName = sanitizeText(p.name);
        const pDesc = sanitizeText(p.description);
        const pPrice = sanitizeText(p.price);
        return `
          <div class="price-row">
            <div class="price-info">
              <h3>${pName}</h3>
              <p>${pDesc}</p>
            </div>
            <span class="price-amount">${pPrice}</span>
          </div>`;
      }).join('');
      sections.push(`
      <section id="pricing" class="section">
        <div class="container">
          <h2 class="section-title">Pricing <span class="neon-underline"></span></h2>
          <div class="price-list">${rows}</div>
        </div>
      </section>`);
    }

    // --- Business Hours ---
    if (enabledFeatures.has('business-hours') && businessHours.length > 0) {
      const hoursList = businessHours.map(h => `<li>${sanitizeText(h)}</li>`).join('');
      sections.push(`
      <section class="section">
        <div class="container">
          <h2 class="section-title">Hours <span class="neon-underline"></span></h2>
          <ul class="hours-list">${hoursList}</ul>
        </div>
      </section>`);
    }

    // --- Contact ---
    if (enabledFeatures.has('contact-form')) {
      sections.push(`
      <section id="contact" class="section">
        <div class="container">
          <h2 class="section-title">Contact <span class="neon-underline"></span></h2>
          <div class="contact-grid">
            <div class="contact-info">
              ${enabledFeatures.has('phone-display') && phone ? `<div class="contact-item">${getIconSvg('phone')}<a href="tel:${escapeAttr(phone)}">${sanitizeText(phone)}</a></div>` : ''}
              ${enabledFeatures.has('address-map') && address ? `<div class="contact-item">${getIconSvg('map-pin')}<span>${sanitizeText(address)}</span></div>` : ''}
              ${enabledFeatures.has('address-map') && googleMapsUrl ? `<a class="map-link" href="${escapeAttr(googleMapsUrl)}" target="_blank" rel="noopener noreferrer">View on Google Maps &rarr;</a>` : ''}
            </div>
            <div class="contact-divider"></div>
            <form class="contact-form" ${contactEmail ? `action="https://formsubmit.co/${escapeAttr(contactEmail)}" method="POST"` : 'onsubmit="return false;"'}>
              <input type="text" placeholder="Your Name" required>
              <input type="email" placeholder="Your Email" required>
              <textarea placeholder="Message" rows="4" required></textarea>
              <button type="submit" class="btn-neon">Send Message</button>
            </form>
          </div>
        </div>
      </section>`);
    }

    return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${safeSeoTitle}</title>
  <meta name="description" content="${safeSeoDesc}">
  <link rel="preconnect" href="https://fonts.googleapis.com">
  <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
  <link href="${fontsUrl}" rel="stylesheet">
  <style>
    *, *::before, *::after { margin: 0; padding: 0; box-sizing: border-box; }
    :root {
      --bg: #0a0a0f; --bg-alt: #0e0e18; --bg-card: #111122;
      --cyan: #00f5d4; --pink: #f15bb5; --yellow: #fee440;
      --text: #e2e8f0; --text-muted: #8892a8;
      --font-heading: 'Outfit', sans-serif; --font-body: 'Inter', sans-serif;
    }
    html { scroll-behavior: smooth; }
    body {
      font-family: var(--font-body); background: var(--bg); color: var(--text); line-height: 1.6;
      background-image:
        linear-gradient(rgba(0,245,212,.03) 1px, transparent 1px),
        linear-gradient(90deg, rgba(0,245,212,.03) 1px, transparent 1px);
      background-size: 60px 60px;
    }
    .container { max-width: 1160px; margin: 0 auto; padding: 0 24px; }
    a { color: var(--cyan); text-decoration: none; transition: opacity .2s; }
    a:hover { opacity: .85; }

    /* NEON UTILITIES */
    .neon-text-cyan { color: var(--cyan); text-shadow: 0 0 7px var(--cyan), 0 0 20px rgba(0,245,212,.4), 0 0 40px rgba(0,245,212,.2); }
    .neon-text-pink { color: var(--pink); text-shadow: 0 0 7px var(--pink), 0 0 20px rgba(241,91,181,.4), 0 0 40px rgba(241,91,181,.2); }
    .neon-underline {
      display: block; width: 60px; height: 3px; margin-top: 12px;
      background: var(--cyan);
      box-shadow: 0 0 8px var(--cyan), 0 0 20px rgba(0,245,212,.4);
      border-radius: 2px;
    }
    .neon-cyan { color: var(--cyan); }

    @keyframes neonFlicker {
      0%, 19%, 21%, 23%, 25%, 54%, 56%, 100% { opacity: 1; }
      20%, 24%, 55% { opacity: .6; }
    }

    /* NAV */
    .navbar {
      position: fixed; top: 0; left: 0; right: 0; z-index: 100;
      background: rgba(10,10,15,.92); backdrop-filter: blur(10px); -webkit-backdrop-filter: blur(10px);
      border-bottom: 1px solid rgba(0,245,212,.15);
    }
    .nav-inner { display: flex; align-items: center; justify-content: space-between; padding: 16px 24px; max-width: 1160px; margin: 0 auto; }
    .nav-brand {
      font-family: var(--font-heading); font-size: 1.4rem; font-weight: 700;
      color: var(--cyan); text-shadow: 0 0 10px rgba(0,245,212,.5);
      letter-spacing: 1px; text-transform: uppercase;
    }
    .nav-links { display: flex; gap: 28px; list-style: none; }
    .nav-links a { color: var(--text-muted); font-size: .85rem; text-transform: uppercase; letter-spacing: 1px; font-weight: 500; font-family: var(--font-heading); transition: color .2s, text-shadow .2s; }
    .nav-links a:hover { color: var(--cyan); text-shadow: 0 0 8px rgba(0,245,212,.4); }
    .nav-toggle { display: none; background: none; border: none; cursor: pointer; }
    .nav-toggle span { display: block; width: 26px; height: 2px; background: var(--cyan); margin: 6px 0; border-radius: 1px; box-shadow: 0 0 6px var(--cyan); }

    /* HERO - CSS grid with staggered/offset cells */
    .hero { position: relative; min-height: 100vh; display: flex; align-items: center; overflow: hidden; }
    .hero-overlay { position: absolute; inset: 0; background: radial-gradient(ellipse at 30% 50%, rgba(0,245,212,.06) 0%, transparent 60%), radial-gradient(ellipse at 70% 50%, rgba(241,91,181,.05) 0%, transparent 60%); }
    .hero-content { position: relative; z-index: 2; padding: 140px 24px 100px; max-width: 1160px; margin: 0 auto; width: 100%; }
    .hero-grid {
      display: grid; grid-template-columns: repeat(4, 1fr); grid-template-rows: auto auto auto auto; gap: 16px;
    }
    .hero-cell-headline {
      grid-column: 1 / 4; grid-row: 1 / 2;
      font-family: var(--font-heading); font-size: clamp(2.6rem, 6vw, 4.5rem); font-weight: 800;
      line-height: 1.1; text-transform: uppercase; letter-spacing: 1px;
      color: var(--cyan);
      text-shadow: 0 0 10px var(--cyan), 0 0 30px rgba(0,245,212,.5), 0 0 60px rgba(0,245,212,.3);
      animation: neonFlicker 4s infinite alternate;
    }
    .hero-cell-sub {
      grid-column: 2 / 5; grid-row: 2 / 3;
      font-size: clamp(1rem, 1.8vw, 1.15rem); color: var(--text-muted); max-width: 500px; line-height: 1.7;
      padding-top: 8px;
    }
    .hero-cell-cta {
      grid-column: 1 / 3; grid-row: 3 / 4;
      padding-top: 16px;
    }
    .hero-cell-image {
      grid-column: 3 / 5; grid-row: 1 / 4;
      display: flex; align-items: center; justify-content: center;
    }
    .hero-cell-image img {
      max-width: 100%; border-radius: 8px;
      border: 2px solid rgba(0,245,212,.3);
      box-shadow: 0 0 30px rgba(0,245,212,.15), 0 0 60px rgba(241,91,181,.1);
    }
    .hero-cta {
      display: inline-block; padding: 16px 40px;
      border: 2px solid var(--cyan); color: var(--cyan); border-radius: 4px;
      font-family: var(--font-heading); font-size: 1rem; font-weight: 600;
      text-transform: uppercase; letter-spacing: 2px;
      box-shadow: 0 0 12px rgba(0,245,212,.3), inset 0 0 12px rgba(0,245,212,.05);
      transition: background .2s, box-shadow .2s, transform .2s;
    }
    .hero-cta:hover { background: rgba(0,245,212,.1); box-shadow: 0 0 24px rgba(0,245,212,.5), inset 0 0 24px rgba(0,245,212,.1); transform: translateY(-2px); opacity: 1; color: var(--cyan); }

    /* SECTIONS */
    .section { padding: 88px 0; }
    .section-title { font-family: var(--font-heading); font-size: clamp(1.8rem, 4vw, 2.5rem); font-weight: 800; text-transform: uppercase; letter-spacing: 1px; margin-bottom: 48px; }

    /* ABOUT - card with stat highlights */
    .about-card { background: var(--bg-card); border: 1px solid rgba(0,245,212,.15); border-radius: 8px; padding: 40px 36px; max-width: 800px; }
    .about-text { font-size: 1.05rem; color: var(--text-muted); line-height: 1.85; margin-bottom: 28px; }
    .about-stats { display: flex; gap: 24px; flex-wrap: wrap; }
    .stat-box { background: rgba(0,245,212,.08); border: 1px solid rgba(0,245,212,.2); border-radius: 6px; padding: 16px 24px; text-align: center; flex: 1; min-width: 100px; }
    .stat-box .stat-value { font-family: var(--font-heading); font-size: 1.6rem; font-weight: 800; color: var(--cyan); text-shadow: 0 0 8px rgba(0,245,212,.4); display: block; }
    .stat-box .stat-label { font-size: .75rem; color: var(--text-muted); text-transform: uppercase; letter-spacing: 1px; }

    /* SERVICES */
    .services-grid { columns: 2; column-gap: 24px; }
    .service-card {
      background: var(--bg-card); border: 1px solid rgba(255,255,255,.06); border-radius: 8px;
      padding: 32px 24px; position: relative; overflow: hidden;
      break-inside: avoid; margin-bottom: 24px;
      transition: border-color .3s, box-shadow .3s;
    }
    .service-top-border { position: absolute; top: 0; left: 0; right: 0; height: 3px; background: var(--card-neon); box-shadow: 0 0 12px var(--card-neon); }
    .service-card:hover { border-color: var(--card-neon); box-shadow: 0 0 20px color-mix(in srgb, var(--card-neon) 25%, transparent); }
    .service-icon { color: var(--card-neon); margin-bottom: 16px; }
    .icon-char { font-size: 28px; line-height: 1; }
    .service-card h3 { font-family: var(--font-heading); font-size: 1.1rem; font-weight: 700; text-transform: uppercase; margin-bottom: 10px; letter-spacing: .5px; }
    .service-card p { color: var(--text-muted); font-size: .9rem; line-height: 1.65; }
    .service-price { display: inline-block; margin-top: 14px; color: var(--cyan); font-weight: 700; font-size: 1.05rem; }

    /* GALLERY */
    .gallery-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; }
    .gallery-item img { width: 100%; height: 220px; object-fit: cover; display: block; border-radius: 6px; border: 1px solid rgba(0,245,212,.15); transition: border-color .3s, box-shadow .3s; }
    .gallery-item:hover img { border-color: var(--cyan); box-shadow: 0 0 16px rgba(0,245,212,.2); }

    /* TESTIMONIALS - horizontal scroll-snap carousel */
    .testimonials-grid { display: flex; gap: 24px; overflow-x: auto; scroll-snap-type: x mandatory; -webkit-overflow-scrolling: touch; padding-bottom: 16px; }
    .testimonials-grid::-webkit-scrollbar { height: 6px; }
    .testimonials-grid::-webkit-scrollbar-track { background: var(--bg-card); border-radius: 3px; }
    .testimonials-grid::-webkit-scrollbar-thumb { background: var(--cyan); border-radius: 3px; }
    .testimonial-card { background: var(--bg-card); border: 1px solid rgba(255,255,255,.06); border-radius: 8px; padding: 32px; position: relative; flex: 0 0 340px; scroll-snap-align: start; }
    .neon-quote { font-size: 3.5rem; line-height: 1; color: var(--pink); text-shadow: 0 0 10px rgba(241,91,181,.4); font-family: Georgia, serif; position: absolute; top: 12px; right: 24px; }
    .testimonial-body { font-size: .98rem; color: var(--text-muted); line-height: 1.7; margin-bottom: 20px; font-style: italic; }
    .testimonial-author strong { display: block; color: var(--text); font-size: .93rem; }
    .testimonial-role { color: var(--text-muted); font-size: .82rem; }
    .testimonial-stars { margin-top: 6px; }
    .star { color: #334155; font-size: 1rem; }
    .star.filled { color: var(--yellow); text-shadow: 0 0 6px rgba(254,228,64,.4); }

    /* FAQ */
    .faq-list { max-width: 760px; }
    .faq-item { border-bottom: 1px solid rgba(0,245,212,.1); padding: 24px 0; }
    .faq-q { font-family: var(--font-heading); font-size: 1.1rem; font-weight: 700; text-transform: uppercase; margin-bottom: 10px; letter-spacing: .5px; }
    .faq-a { color: var(--text-muted); line-height: 1.7; font-size: .93rem; }

    /* PRICE LIST */
    .price-list { max-width: 760px; }
    .price-row { display: flex; justify-content: space-between; align-items: center; padding: 22px 0; border-bottom: 1px solid rgba(0,245,212,.1); gap: 24px; }
    .price-info h3 { font-family: var(--font-heading); font-size: 1.05rem; font-weight: 700; text-transform: uppercase; margin-bottom: 4px; }
    .price-info p { color: var(--text-muted); font-size: .88rem; }
    .price-amount { font-family: var(--font-heading); font-size: 1.4rem; font-weight: 800; color: var(--cyan); text-shadow: 0 0 8px rgba(0,245,212,.3); white-space: nowrap; }

    /* HOURS */
    .hours-list { list-style: none; max-width: 400px; }
    .hours-list li { padding: 12px 0; border-bottom: 1px solid rgba(0,245,212,.08); color: var(--text-muted); font-size: .93rem; }

    /* GOOGLE REVIEWS */
    .google-reviews { background: var(--bg-alt); border-bottom: 1px solid rgba(0,245,212,.1); padding: 18px 0; }
    .review-badge { display: flex; align-items: center; justify-content: center; gap: 12px; flex-wrap: wrap; }
    .review-badge .star.filled { color: var(--yellow); text-shadow: 0 0 6px rgba(254,228,64,.4); }
    .review-text { color: var(--text-muted); font-weight: 600; font-size: .9rem; }

    /* PROMO */
    .promo-banner { background: var(--bg-alt); padding: 18px 0; border-bottom: 1px solid rgba(241,91,181,.1); }
    .promo-inner { display: flex; align-items: center; gap: 20px; flex-wrap: wrap; }
    .promo-badge { border: 1px solid var(--cyan); padding: 6px 18px; border-radius: 4px; font-family: var(--font-heading); font-weight: 700; font-size: .95rem; text-shadow: 0 0 6px rgba(0,245,212,.4); box-shadow: 0 0 10px rgba(0,245,212,.2); }
    .promo-content strong { display: block; margin-bottom: 2px; }
    .promo-content span { color: var(--text-muted); font-size: .88rem; }

    /* CONTACT - split with glowing accent divider */
    .contact-grid { display: grid; grid-template-columns: 1fr 4px 1fr; gap: 32px; align-items: start; }
    .contact-divider { background: var(--cyan); box-shadow: 0 0 12px var(--cyan), 0 0 24px rgba(0,245,212,.4); border-radius: 2px; align-self: stretch; }
    .contact-info { display: flex; flex-direction: column; gap: 20px; }
    .contact-item { display: flex; align-items: center; gap: 14px; color: var(--text-muted); }
    .contact-item .icon-char { color: var(--cyan); flex-shrink: 0; font-size: 20px; line-height: 1; }
    .contact-item a { color: var(--text); }
    .map-link { margin-top: 8px; font-weight: 600; font-size: .88rem; letter-spacing: .5px; text-transform: uppercase; font-family: var(--font-heading); }
    .contact-form { display: flex; flex-direction: column; gap: 16px; }
    .contact-form input, .contact-form textarea {
      background: var(--bg-card); border: 1px solid rgba(0,245,212,.15); border-radius: 6px;
      color: var(--text); padding: 14px 16px; font-family: var(--font-body); font-size: .93rem; outline: none; transition: border-color .2s, box-shadow .2s;
    }
    .contact-form input:focus, .contact-form textarea:focus { border-color: var(--cyan); box-shadow: 0 0 10px rgba(0,245,212,.15); }
    .btn-neon {
      padding: 16px; border: 2px solid var(--cyan); background: transparent; color: var(--cyan);
      font-family: var(--font-heading); font-size: .95rem; font-weight: 700; text-transform: uppercase; letter-spacing: 2px;
      border-radius: 4px; cursor: pointer; box-shadow: 0 0 12px rgba(0,245,212,.2);
      transition: background .2s, box-shadow .2s, transform .2s;
    }
    .btn-neon:hover { background: rgba(0,245,212,.1); box-shadow: 0 0 24px rgba(0,245,212,.4); transform: translateY(-2px); }

    /* FOOTER */
    .footer { background: var(--bg-alt); border-top: 1px solid rgba(0,245,212,.1); padding: 44px 0; text-align: center; }
    .footer-brand { font-family: var(--font-heading); font-size: 1.1rem; color: var(--cyan); text-shadow: 0 0 8px rgba(0,245,212,.4); text-transform: uppercase; letter-spacing: 2px; margin-bottom: 8px; }
    .footer p { color: var(--text-muted); font-size: .83rem; }

    /* RESPONSIVE */
    @media (max-width: 768px) {
      .nav-links { display: none; flex-direction: column; position: absolute; top: 100%; left: 0; right: 0; background: rgba(10,10,15,.98); padding: 16px 24px; gap: 16px; }
      .nav-links.show { display: flex; }
      .nav-toggle { display: block; }
      .hero-grid { grid-template-columns: 1fr; }
      .hero-cell-headline { grid-column: 1; grid-row: auto; }
      .hero-cell-sub { grid-column: 1; grid-row: auto; }
      .hero-cell-cta { grid-column: 1; grid-row: auto; text-align: center; }
      .hero-cell-image { grid-column: 1; grid-row: auto; }
      .services-grid { columns: 1; }
      .gallery-grid { grid-template-columns: repeat(2, 1fr); }
      .contact-grid { grid-template-columns: 1fr; }
      .contact-divider { display: none; }
      .testimonials-grid { flex-wrap: nowrap; }
      .testimonial-card { flex: 0 0 280px; }
      .price-row { flex-direction: column; align-items: flex-start; gap: 8px; }
    }
    @media (max-width: 480px) {
      .gallery-grid { grid-template-columns: 1fr; }
      .hero-content { padding: 110px 16px 60px; }
    }
  </style>
</head>
<body>
  <nav class="navbar">
    <div class="nav-inner">
      <a href="#" class="nav-brand">${safeName}</a>
      <button class="nav-toggle" aria-label="Toggle menu" onclick="this.parentElement.querySelector('.nav-links').classList.toggle('show')">
        <span></span><span></span><span></span>
      </button>
      <ul class="nav-links">
        ${navLinks.map(l => `<li><a href="${l.href}">${l.label}</a></li>`).join('')}
      </ul>
    </div>
  </nav>

  <section class="hero">
    <div class="hero-overlay"></div>
    <div class="hero-content">
      <div class="hero-grid">
        <div class="hero-cell-headline">${safeHeadline}</div>
        <div class="hero-cell-sub">${truncateAtSentence(safeAbout)}</div>
        <div class="hero-cell-cta">
          ${enabledFeatures.has('contact-form') ? `<a href="#contact" class="hero-cta">Get Started</a>` : ''}
        </div>
        ${heroImage ? `<div class="hero-cell-image"><img src="${escapeAttr(heroImage)}" alt="${safeName}" loading="lazy"></div>` : ''}
      </div>
    </div>
  </section>

  ${sections.join('\n')}

  <footer class="footer">
    <div class="container">
      <div class="footer-brand">${safeName}</div>
      <p>${safeName}</p>
    </div>
  </footer>
</body>
</html>`;
  }
};
